/**
 * OCC API service (S4.2 read models + S4.3 workbench).
 *
 * The lens endpoints are PURE PROJECTIONS served by `mocks/handlers/occ.ts`
 * from the shared selectors in `src/lib/occ/selectors.ts` — the exact
 * functions the UI types below come from, so every number on an OCC lens
 * equals its source-store number by construction. OCC itself never writes:
 * the only mutating methods here (`updateLine` / `resolveException`) are the
 * S3.2 vendor-checkpoint seam used by the PO module, not by the workbench.
 */

import { isAxiosError } from 'axios';
import apiClient from './axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import type {
  FulfillmentNodeCode,
  FulfillmentNodeConfig,
  OccLine,
  OccNode,
} from '@/types/api';
import type {
  OccBatchLensRow,
  OccBoardStats,
  OccExceptionReason,
  OccExceptionRow,
  OccExceptionSummary,
  OccInvoiceStatusRow,
  OccLensLine,
  OccOrderRollupRow,
  OccReceiptStatusRow,
} from '@/lib/occ/selectors';
import type { FulfillmentOwnerRole } from '@/types/api/occ';

// F2.3 (D-CAT-17 pattern, house standard for live-seam resilience): only
// "route does not exist" responses count as lens-unavailable; transient
// failures (5xx, timeouts) must keep propagating.
const isEndpointMissingError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.status === 404 || error.response?.status === 501);

// ---------------------------------------------------------------------------
// Read-model payloads (mirror mocks/handlers/occ.ts responses)
// ---------------------------------------------------------------------------

/**
 * `/occ/board` — stats + per-SO 訂單進度總覽 roll-up (+ the legacy seam keys).
 *
 * `summary`/`stats`/`orders`/`syncedAt` are OPTIONAL because the live gold
 * seam serves the board as a bare `{data, pagination}` page (drift D-OCC-G1)
 * — absent fields stay ABSENT on the read model (hidden tiles / em-dash in
 * the UI), they are NEVER filled with zeros (a zero would be a false data
 * claim). Simplifies to the required shape at CU-2 (B3.3' read models);
 * the absent-≠-zero vitest stays either way.
 */
export interface OccBoardReadModel {
  /** Legacy S3.2 seam rows — `[]` when the seam is empty. */
  data: OccLine[];
  /** Legacy per-node counts — absent when the serving seam does not project them. */
  summary?: Record<OccNode, number>;
  stats?: OccBoardStats;
  orders?: OccOrderRollupRow[];
  syncedAt?: string;
}

/**
 * F2.3 board-shape adapter (PERMANENT belt-and-braces; passthrough at CU-2).
 *
 * A complete MSW-shaped payload passes through IDENTICALLY (mock mode is
 * byte-identical by construction). Gold's `{data, pagination}` page maps to
 * a partial read model whose `summary`/`stats`/`orders`/`syncedAt` stay
 * absent — never zero-filled.
 */
export const adaptOccBoardPayload = (raw: unknown): OccBoardReadModel => {
  const payload = (raw ?? {}) as Partial<OccBoardReadModel>;
  if (
    payload.stats !== undefined &&
    payload.orders !== undefined &&
    payload.syncedAt !== undefined
  ) {
    // Complete read model (MSW / post-CU-2 backend) — identity passthrough.
    return raw as OccBoardReadModel;
  }
  return {
    data: Array.isArray(payload.data) ? payload.data : [],
    ...(payload.summary !== undefined ? { summary: payload.summary } : {}),
    ...(payload.stats !== undefined ? { stats: payload.stats } : {}),
    ...(payload.orders !== undefined ? { orders: payload.orders } : {}),
    ...(payload.syncedAt !== undefined ? { syncedAt: payload.syncedAt } : {}),
  };
};

export interface OccLensLinesParams {
  soId?: string;
  node?: FulfillmentNodeCode;
  hasException?: boolean;
}

export interface OccLensLinesResponse {
  data: OccLensLine[];
  syncedAt: string;
}

export interface OccExceptionsParams {
  reason?: OccExceptionReason;
  owner?: FulfillmentOwnerRole;
}

export interface OccExceptionsResponse {
  data: OccExceptionRow[];
  summary: OccExceptionSummary;
  syncedAt: string;
}

export interface OccBatchLensResponse {
  data: OccBatchLensRow[];
  syncedAt: string;
}

export interface OccInvoiceStatusResponse {
  data: OccInvoiceStatusRow[];
  source: 'finance';
  syncedAt: string;
}

export interface OccReceiptStatusResponse {
  data: OccReceiptStatusRow[];
  source: 'shipping';
  syncedAt: string;
}

export interface OccFulfillmentNodesResponse {
  data: FulfillmentNodeConfig[];
}

export const occApi = {
  board: async (node?: OccNode): Promise<OccBoardReadModel> => {
    const { data } = await apiClient.get<unknown>(API_ROUTES.OCC_BOARD, {
      params: node ? { node } : undefined,
    });
    return adaptOccBoardPayload(data);
  },

  /** S4.2 lens rows — projected FulfillmentLine views + SO/product context. */
  lensLines: async (params?: OccLensLinesParams): Promise<OccLensLinesResponse> => {
    const { data } = await apiClient.get<OccLensLinesResponse>('/occ/lines', {
      params: params
        ? {
            soId: params.soId,
            node: params.node,
            hasException: params.hasException ? 'true' : undefined,
          }
        : undefined,
    });
    return data;
  },

  /** S4.2 訂單異常 worklist — derived per read, auto-clears, no close endpoint. */
  exceptions: async (params?: OccExceptionsParams): Promise<OccExceptionsResponse> => {
    const { data } = await apiClient.get<OccExceptionsResponse>('/occ/exceptions', {
      params,
    });
    return data;
  },

  /** S4.2 裝櫃批次 lens — projected from the shipping store on every read. */
  batchLens: async (): Promise<OccBatchLensResponse> => {
    const { data } = await apiClient.get<OccBatchLensResponse>('/occ/batch-lens');
    return data;
  },

  /** Referenced lens 開票狀態 — 來源:會計, read-only. */
  invoiceStatus: async (): Promise<OccInvoiceStatusResponse> => {
    const { data } = await apiClient.get<OccInvoiceStatusResponse>('/occ/invoice-status');
    return data;
  },

  /** Referenced lens 收貨狀態 — 來源:船務, read-only. */
  receiptStatus: async (): Promise<OccReceiptStatusResponse> => {
    const { data } = await apiClient.get<OccReceiptStatusResponse>('/occ/receipt-status');
    return data;
  },

  /** Late-bound 進度點 table — chips/rails render these labels, never raw codes. */
  fulfillmentNodes: async (): Promise<OccFulfillmentNodesResponse> => {
    const { data } = await apiClient.get<OccFulfillmentNodesResponse>('/occ/fulfillment-nodes');
    return data;
  },

  // -- S3.2 vendor-checkpoint seam (PO module writes; OCC never calls) ----

  updateLine: async (id: string, body: Partial<OccLine>): Promise<{ data: OccLine }> => {
    const { data } = await apiClient.patch<{ data: OccLine }>(`/occ/lines/${id}`, body);
    return data;
  },

  resolveException: async (
    id: string,
    body: { vendorId?: number; vendorName?: string; note?: string }
  ): Promise<{ data: OccLine }> => {
    const { data } = await apiClient.post<{ data: OccLine }>(
      `/occ/lines/${id}/resolve-exception`,
      body
    );
    return data;
  },
};

// ---------------------------------------------------------------------------
// F2.3 lens-availability guard (D-CAT-17 pattern, PERMANENT house standard)
// ---------------------------------------------------------------------------

/** A lens read that survived, or a lens whose serving route is absent. */
export interface OccLensResult<T> {
  payload?: T;
  /** true ONLY when the serving route is MISSING (404/501) on this seam. */
  unavailable: boolean;
}

const lensGuard = async <T>(call: () => Promise<T>): Promise<OccLensResult<T>> => {
  try {
    return { payload: await call(), unavailable: false };
  } catch (error) {
    if (!isEndpointMissingError(error)) {
      throw error;
    }
    console.warn(
      '[occLensApi] lens endpoint unavailable on this seam — surfacing degraded state instead of a page crash:',
      error
    );
    return { unavailable: true };
  }
};

/**
 * Guarded lens reads for the workbench: each settles independently (the
 * `Promise.allSettled` semantics of the D-CAT-17 guard, applied per lens
 * call) — a MISSING route degrades that ONE lens to a surfaced unavailable
 * state while every other lens keeps rendering; transient failures (5xx)
 * still propagate into the normal error state.
 */
export const occLensApi = {
  board: (node?: OccNode) => lensGuard(() => occApi.board(node)),
  lensLines: (params?: OccLensLinesParams) => lensGuard(() => occApi.lensLines(params)),
  exceptions: (params?: OccExceptionsParams) => lensGuard(() => occApi.exceptions(params)),
  batchLens: () => lensGuard(() => occApi.batchLens()),
  invoiceStatus: () => lensGuard(() => occApi.invoiceStatus()),
  receiptStatus: () => lensGuard(() => occApi.receiptStatus()),
  fulfillmentNodes: () => lensGuard(() => occApi.fulfillmentNodes()),
};

export default occApi;
